import { useState, useEffect, useCallback } from "react";
import { Plug, Play, CheckCircle2, AlertCircle, Clock, Loader2, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";
import { toast } from "sonner";

const ssApi = {
  getSettings: () => api.get("/admin/springserve/settings"),
  updateSettings: (data) => api.put("/admin/springserve/settings", data),
  testConnection: () => api.post("/admin/springserve/test-connection"),
  sync: (data) => api.post("/admin/springserve/sync", data),
  getSyncLogs: () => api.get("/admin/springserve/sync-logs"),
};

export default function SpringServePage() {
  const [settings, setSettings] = useState({ email: "", password_set: false, daily_sync_enabled: false, daily_sync_time: "11:00" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dailySyncEnabled, setDailySyncEnabled] = useState(false);
  const [dailySyncTime, setDailySyncTime] = useState("11:00");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connected, setConnected] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [logs, setLogs] = useState([]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await ssApi.getSettings();
      setSettings(res.data);
      setEmail(res.data.email || "");
      setDailySyncEnabled(res.data.daily_sync_enabled || false);
      setDailySyncTime(res.data.daily_sync_time || "11:00");
    } catch {}
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await ssApi.getSyncLogs();
      setLogs(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchSettings(); fetchLogs(); }, [fetchSettings, fetchLogs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { email, daily_sync_enabled: dailySyncEnabled, daily_sync_time: dailySyncTime };
      if (password) data.password = password;
      await ssApi.updateSettings(data);
      toast.success("Settings saved");
      setPassword("");
      fetchSettings();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setConnected(null);
    try {
      await ssApi.testConnection();
      setConnected(true);
      toast.success("SpringServe connection successful");
    } catch {
      setConnected(false);
      toast.error("Connection failed");
    }
    setTesting(false);
  };

  const handleSync = async () => {
    if (!startDate || !endDate) { toast.error("Select date range"); return; }
    setSyncing(true);
    try {
      const res = await ssApi.sync({ start_date: startDate, end_date: endDate });
      toast.success(`Synced: ${res.data.rows_inserted} inserted, ${res.data.rows_updated} updated`);
      fetchLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    }
    setSyncing(false);
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="springserve-page">
      <div>
        <h1 className="text-xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="springserve-title">
          SpringServe
        </h1>
        <p className="text-xs text-slate-500 mt-0.5">Manage SpringServe ad server connection and sync revenue data</p>
      </div>

      {/* Credentials */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="springserve-credentials">
        <h3 className="text-sm font-semibold text-slate-900">API Credentials</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">API Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user+api@company.com" className="h-9 text-sm" data-testid="springserve-email" />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Password {settings.password_set && <span className="text-emerald-500 text-[10px]">(set)</span>}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={settings.password_set ? "Leave blank to keep current" : "Enter API password"} className="h-9 text-sm" data-testid="springserve-password" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="springserve-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
            Save Settings
          </Button>
          <Button size="sm" variant="outline" onClick={handleTest} disabled={testing} data-testid="springserve-test-btn">
            {testing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plug size={14} className="mr-1" />}
            Test Connection
          </Button>
          {connected === true && <span className="flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 size={14} /> Connected</span>}
          {connected === false && <span className="flex items-center gap-1 text-xs text-red-500"><AlertCircle size={14} /> Failed</span>}
        </div>
      </div>

      {/* Auto Sync */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="springserve-auto-sync">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-900">Daily Auto-Sync</h3>
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <Switch checked={dailySyncEnabled} onCheckedChange={setDailySyncEnabled} data-testid="springserve-auto-sync-toggle" />
            <span className="text-sm text-slate-600">{dailySyncEnabled ? "Enabled" : "Disabled"}</span>
          </div>
          {dailySyncEnabled && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500">Sync Time (UTC)</Label>
              <Input type="time" value={dailySyncTime} onChange={(e) => setDailySyncTime(e.target.value)} className="h-8 text-sm w-[120px]" data-testid="springserve-sync-time" />
            </div>
          )}
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <p className="text-[11px] text-slate-400">Automatically syncs yesterday's data daily.</p>
          <Button onClick={handleSave} disabled={saving} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="springserve-save-sync-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Save Settings
          </Button>
        </div>
      </div>

      {/* Manual Sync */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="springserve-manual-sync">
        <h3 className="text-sm font-semibold text-slate-900">Manual Sync</h3>
        <div className="flex items-center gap-3">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Start Date</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9 text-sm w-[160px]" data-testid="springserve-start-date" />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">End Date</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-9 text-sm w-[160px]" data-testid="springserve-end-date" />
          </div>
          <div className="pt-5">
            <Button size="sm" onClick={handleSync} disabled={syncing} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="springserve-sync-btn">
              {syncing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Play size={14} className="mr-1" />}
              {syncing ? "Syncing..." : "Sync Now"}
            </Button>
          </div>
        </div>
      </div>

      {/* Sync History */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="springserve-sync-history">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Sync History</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Time</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Type</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Date Range</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4 text-right">Rows</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4 text-right">Duration</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400 text-sm">No sync history yet</TableCell></TableRow>
            ) : logs.map((log, i) => (
              <TableRow key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                <TableCell className="py-2.5 px-4 text-xs text-slate-500">{new Date(log.started_at).toLocaleString()}</TableCell>
                <TableCell className="py-2.5 px-4">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${log.sync_type === "manual" ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-600"}`}>
                    {log.sync_type}
                  </span>
                </TableCell>
                <TableCell className="py-2.5 px-4 text-xs text-slate-600">{log.start_date} → {log.end_date}</TableCell>
                <TableCell className="py-2.5 px-4">
                  {log.status === "success" ? (
                    <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 size={12} /> Success</span>
                  ) : (
                    <span className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} /> Error</span>
                  )}
                </TableCell>
                <TableCell className="py-2.5 px-4 text-xs text-right text-slate-700">{log.rows_inserted + log.rows_updated}</TableCell>
                <TableCell className="py-2.5 px-4 text-xs text-right text-slate-500">{log.duration_seconds}s</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
